import { h, Fragment } from 'preact';
import styles from './styles.module.scss';
import { PointsBadgeIcon } from '../../svg/icons/points-badge';
import { UseDefaultUserIcon } from '../../svg/icons/default-user';
import { BadgeCheckIcon } from '../../svg/icons/badge-check';
import { BadgeEyesIcon } from '../../svg/icons/badge-eyes';
import { BadgeHeartIcon } from '../../svg/icons/badge-heart';
import { BadgeTrendingIcon } from '../../svg/icons/badge-trending';
import { BadgeReplyIcon } from '../../svg/icons/badge-reply';
import { BadgeMentionIcon } from '../../svg/icons/badge-mention';
import { BadgeTrophyIcon } from '../../svg/icons/badge-trophy';
import { getUserName } from './notification-utils';

export const NotificationPostTitle = ({ title = '' }) => {
  if (title.length > 65) {
    title = `${title.substr(0, 65).trim()}...`;
  }

  return (
    <span
      className={styles.notificationPostTitle}
      dangerouslySetInnerHTML={{ __html: title }}
    />
  );
};

/**
 * Shared visual scaffold every notification type renders into: an image
 * (with an optional icon badge overlaid on it), an optional title, an
 * optional body, and a "time ago" string.
 */
export function NotificationLayout({ image, badge, title, body, time, tag }) {
  return (
    <div className={styles.notificationRow}>
      <div className={styles.notificationImageWrapper}>
        {image}
        {badge && (
          <span className={styles.notificationImageBadge} aria-hidden={true}>
            {badge}
          </span>
        )}
      </div>
      <div className={styles.notificationContent}>
        {title && <p className={styles.notificationItemTitle}>{title}</p>}
        {body && <p className={styles.notificationItemBody}>{body}</p>}
        {time && <span className={styles.notificationItemTime}>{time}</span>}
      </div>
      {tag && (
        // not a control: the whole notification row is already the clickable element
        <span className={styles.notificationTag} aria-hidden={true}>
          {tag}
        </span>
      )}
    </div>
  );
}

const PointsImage = () => (
  <PointsBadgeIcon className={styles.pointsImage} width={46} height={46} />
);

// renders the post's thumbnail when the API provides one, points image otherwise
const PostThumbnailImage = ({ buzz_thumbnail }) => {
  const src = buzz_thumbnail && buzz_thumbnail.square;
  return src ? (
    <div className={styles.postThumbnailImage}>
      <img className={styles.postThumbnailImageImg} src={src} alt="" />
    </div>
  ) : (
    <PointsImage />
  );
};

// renders the actor's real avatar when the API provides one, default icon otherwise
const AvatarImage = ({ actor = {} }) => (
  <div className={styles.avatarImage}>
    {actor.avatar_url ? (
      <img
        className={styles.avatarImageImg}
        src={actor.avatar_url}
        alt={getUserName(actor)}
      />
    ) : (
      <UseDefaultUserIcon width={22} height={22} fill="#9b9b9b" />
    )}
  </div>
);

export function IPAPostPublishNotification({ title, time, buzz_thumbnail }) {
  return (
    <NotificationLayout
      image={<PostThumbnailImage buzz_thumbnail={buzz_thumbnail} />}
      badge={<BadgeCheckIcon />}
      body={
        <Fragment>
          You Published <NotificationPostTitle title={title} />
        </Fragment>
      }
      time={time}
      tag="Share"
    />
  );
}

export function IPAViewThresholdNotification({
  title,
  threshold,
  time,
  buzz_thumbnail,
}) {
  return (
    <NotificationLayout
      image={<PostThumbnailImage buzz_thumbnail={buzz_thumbnail} />}
      badge={<BadgeEyesIcon />}
      body={
        <Fragment>
          Your Post <NotificationPostTitle title={title} /> Got {threshold}{' '}
          Views!
        </Fragment>
      }
      time={time}
    />
  );
}

export function IPACommentHeartThresholdNotification({
  title,
  threshold,
  time,
  buzz_thumbnail,
}) {
  return (
    <NotificationLayout
      image={<PostThumbnailImage buzz_thumbnail={buzz_thumbnail} />}
      badge={<BadgeHeartIcon />}
      body={
        <Fragment>
          Your Comment On <NotificationPostTitle title={title} /> Got{' '}
          {threshold} hearts!
        </Fragment>
      }
      time={time}
    />
  );
}

export function IPAPostPromotedNotification({ title, time, buzz_thumbnail }) {
  return (
    <NotificationLayout
      image={<PostThumbnailImage buzz_thumbnail={buzz_thumbnail} />}
      badge={<BadgeTrendingIcon />}
      body={
        <Fragment>
          Your Post <NotificationPostTitle title={title} /> Was Promoted.
        </Fragment>
      }
      time={time}
    />
  );
}

export function CommentReplyNotification({ reply_guy = {}, title, time }) {
  return (
    <NotificationLayout
      image={<AvatarImage actor={reply_guy} />}
      badge={<BadgeReplyIcon />}
      title={getUserName(reply_guy)}
      body={
        <Fragment>
          Replied To Your Comment On <NotificationPostTitle title={title} />
        </Fragment>
      }
      time={time}
    />
  );
}

export function CommentMentionNotification({ mentioned_by = {}, title, time }) {
  return (
    <NotificationLayout
      image={<AvatarImage actor={mentioned_by} />}
      badge={<BadgeMentionIcon />}
      title={getUserName(mentioned_by)}
      body={
        <Fragment>
          Mentioned You On <NotificationPostTitle title={title} />
        </Fragment>
      }
      time={time}
    />
  );
}

export function TrophyAddedNotification({ trophy_name, trophy_img_url, time }) {
  return (
    <NotificationLayout
      badge={<BadgeTrophyIcon />}
      image={
        <div className={styles.trophyImage}>
          <img
            alt={trophy_name}
            className={styles.trophyImageImg}
            src={`${trophy_img_url}?resize=44:44`}
          />
        </div>
      }
      title={`You Earned ${trophy_name} Trophy`}
      time={time}
    />
  );
}

export function DefaultNotification({ time }) {
  return (
    <NotificationLayout
      image={<PointsImage />}
      title="You have a new notification!"
      time={time}
    />
  );
}

export const NOTIFICATION_COMPONENT_TYPES = {
  comments_new_reply: CommentReplyNotification,
  comments_new_mention: CommentMentionNotification,
  ipa_post_promote: IPAPostPromotedNotification,
  ipa_post_publish: IPAPostPublishNotification,
  ipa_view_threshold: IPAViewThresholdNotification,
  ipa_comment_heart_threshold: IPACommentHeartThresholdNotification,
  ipa_trophy_added: TrophyAddedNotification,
};
